import type {
  AnalyticsEventBase,
  AudioChangeEvent,
  CustomDataValuesEvent,
  ErrorEvent,
  SourceChangeEvent,
  VideoChangeEvent,
} from '../types/EventData';

/**
 * If we specify the name of our initial state, then an implicit 'startup' initial event will be created by JSM
 * and fired when the state machine is constructed.
 *
 * Because that implicit JSM initial event is called 'startup', it could cause confusion with our "STARTUP state", so for
 * that reason we will use custom JSM initial event name, to be able to distinguish it from our other {@link Event}s.
 *
 * This event should not be used in the application code, it is only used by the JSM library!
 *
 * @see https://github.com/jakesgordon/javascript-state-machine/blob/2.4.0/README.md#initialization-options
 */
export const JSM_INITIAL_EVENT = 'jsm-initial';

export enum Event {
  AUDIO_CHANGE = 'audioChange',
  AUDIOTRACK_CHANGED = 'audioTrackChanged',
  CUSTOM_DATA_CHANGE = 'customDataChange',
  /**
   * Fired when the player has finished playback.
   */
  END = 'end',
  END_AD = 'adEnd',
  END_BUFFERING = 'endBuffering',
  END_CAST = 'endCasting',
  END_FULLSCREEN = 'endFullscreen',
  ERROR = 'playerError',
  /**
   * Manually fired when a source load with autoplay is detected.
   * Required for accurate VST (video startup time) measurement: Some players delay sending PLAY events until
   * manifest/content is loaded, making VST unreliable if calculated from player events alone.
   * For autoplay scenarios, VST is measured as the time from source load to the first
   * PLAYING or TIMEUPDATE event from the player.
   */
  LOADSTART_AUTOPLAY = 'loadstartAutoplay',
  MANUAL_SOURCE_CHANGE = 'manualSourceChangeInitiated',
  MUTE = 'mute',
  PAUSE = 'pause',
  PLAY = 'play',
  PLAYING = 'playing',
  /**
   * Fired when the player is ready to play.
   */
  READY = 'ready',
  SCREEN_RESIZE = 'resize',
  SEEK = 'seek',
  SEEKED = 'seeked',
  SOURCE_LOADED = 'sourceLoaded',
  SOURCE_UNLOADED = 'sourceUnloaded',
  START_AD = 'adStart',
  START_BUFFERING = 'startBuffering',
  START_CAST = 'startCasting',
  START_FULLSCREEN = 'startFullscreen',
  SUBTITLE_CHANGE = 'subtitleChange',
  TIMECHANGED = 'timechanged',
  UN_MUTE = 'unMute',
  /**
   * Fired when page with player is closed or when the player is unloaded/destroyed.
   */
  UNLOAD = 'unload',
  VIDEO_CHANGE = 'videoChange',
  VIDEOSTART_TIMEOUT = 'VIDEOSTART_TIMEOUT',
}

export interface EventMap {
  [Event.READY]: AnalyticsEventBase;
  [Event.SOURCE_LOADED]: AnalyticsEventBase;
  [Event.PLAY]: AnalyticsEventBase;
  [Event.PLAYING]: AnalyticsEventBase | CustomDataValuesEvent;
  [Event.PAUSE]: AnalyticsEventBase | CustomDataValuesEvent;
  [Event.TIMECHANGED]: AnalyticsEventBase;
  [Event.SEEK]: AnalyticsEventBase;
  [Event.SEEKED]: AnalyticsEventBase;
  [Event.START_CAST]: AnalyticsEventBase;
  [Event.END_CAST]: AnalyticsEventBase;
  [Event.START_BUFFERING]: AnalyticsEventBase;
  [Event.END_BUFFERING]: AnalyticsEventBase;
  [Event.AUDIOTRACK_CHANGED]: AnalyticsEventBase;
  [Event.AUDIO_CHANGE]: AudioChangeEvent;
  [Event.VIDEO_CHANGE]: VideoChangeEvent;
  [Event.START_FULLSCREEN]: AnalyticsEventBase;
  [Event.END_FULLSCREEN]: AnalyticsEventBase;
  [Event.START_AD]: AnalyticsEventBase;
  [Event.SUBTITLE_CHANGE]: AnalyticsEventBase;
  [Event.END_AD]: AnalyticsEventBase;
  [Event.MUTE]: AnalyticsEventBase;
  [Event.UN_MUTE]: AnalyticsEventBase;
  [Event.ERROR]: ErrorEvent;
  [Event.SCREEN_RESIZE]: AnalyticsEventBase;
  [Event.UNLOAD]: AnalyticsEventBase;
  [Event.END]: AnalyticsEventBase;
  [Event.SOURCE_UNLOADED]: AnalyticsEventBase;
  [Event.MANUAL_SOURCE_CHANGE]: SourceChangeEvent;
  [Event.VIDEOSTART_TIMEOUT]: AnalyticsEventBase;
  [Event.CUSTOM_DATA_CHANGE]: AnalyticsEventBase;
  [Event.LOADSTART_AUTOPLAY]: AnalyticsEventBase;
}
